import { attach, sample } from 'effector'
import { and } from 'patronum'
import type { SendEventParams } from '~/shared/analytix'
import {
  reportPromoBannerClick,
  reportPromoBannerDismiss,
  reportPromoBannerImpression,
  type PromoBannerClickPayload,
  type PromoBannerDismissPayload,
  type PromoBannerImpressionPayload,
} from '~/widgets/popup-and-banners/bannerAnalytics'
import { logErrorFx } from '../lib'
import { $readyAllInfo } from '../session/model'
import { $analytix, $analytixEnabled } from '../wrapper'

const $bannerAnalytixCanSend = and($readyAllInfo, $analytixEnabled)

const toBannerImpression = (
  d: PromoBannerImpressionPayload
): SendEventParams.BannerImpression => ({
  pageTitle: d.pageScreenTitle,
  triggerType: d.triggerType,
  bannerType: d.bannerType,
  playerSessionId: d.playerSessionId,
})

const toBannerClick = (
  d: PromoBannerClickPayload
): SendEventParams.BannerClick => ({
  pageTitle: d.pageScreenTitle,
  triggerType: d.triggerType,
  bannerType: d.bannerType,
  redirectDestination: d.storeDestination ?? '',
  playerSessionId: d.playerSessionId,
})

const toBannerDismiss = (
  d: PromoBannerDismissPayload
): SendEventParams.BannerDismiss => ({
  pageTitle: d.pageScreenTitle,
  triggerType: d.triggerType,
  bannerType: d.bannerType,
  playerSessionId: d.playerSessionId,
})

export const sendBannerImpressionFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.BannerImpression) =>
    analytix.sendBannerImpressionEvent(params),
})

export const sendBannerClickFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.BannerClick) =>
    analytix.sendBannerClickEvent(params),
})

export const sendBannerDismissFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.BannerDismiss) =>
    analytix.sendBannerDismissEvent(params),
})

for (const fx of [
  sendBannerImpressionFx,
  sendBannerClickFx,
  sendBannerDismissFx,
]) {
  sample({
    clock: fx.failData,
    fn: (e) => e,
    target: logErrorFx,
  })
}

sample({
  clock: reportPromoBannerImpression,
  source: { canSend: $bannerAnalytixCanSend },
  filter: ({ canSend }) => Boolean(canSend),
  fn: (_, d) => toBannerImpression(d),
  target: sendBannerImpressionFx,
})

sample({
  clock: reportPromoBannerClick,
  source: { canSend: $bannerAnalytixCanSend },
  filter: ({ canSend }) => Boolean(canSend),
  fn: (_, d) => toBannerClick(d),
  target: sendBannerClickFx,
})

sample({
  clock: reportPromoBannerDismiss,
  source: { canSend: $bannerAnalytixCanSend },
  filter: ({ canSend }) => Boolean(canSend),
  fn: (_, d) => toBannerDismiss(d),
  target: sendBannerDismissFx,
})
